import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  chatWebsocketService,
  ChatMessage,
  ChatConnectionState,
} from '@/services/websocket/chat-websocket-service';
import { useChatUIStore } from '@/stores/chat-ui-store';
import { NodeEventType } from '@/types/workflow-types';
import { useWorkflowPanelStore } from '@/stores/workflow-panel-store';
import { accumulateStreamingText } from '@/utils/accumulate-streaming-text';
import type { ChatMessageData } from '@/components/chat';

interface UseNodeChatModeOptions {
  selectedNodeId: string | null;
  applyMessage: (
    updater: (prev: ChatMessageData[]) => ChatMessageData[],
  ) => void;
  insertDivider: (content: string) => void;
  ensureConnection: () => void;
}

export function useNodeChatMode({
  selectedNodeId,
  applyMessage,
  insertDivider,
  ensureConnection,
}: UseNodeChatModeOptions) {
  const {
    isChatConnecting,
    isChatConnected,
    isChatDenied,
    isChatAwaitingInput,
    setChatConnected,
    setChatDenied,
    setChatAwaitingInput,
    resetChatUI,
  } = useChatUIStore();
  const { hidePreview } = useWorkflowPanelStore();

  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isNodeChatSessionActive, setIsNodeChatSessionActive] = useState(false);

  // Track streaming message for accumulation (live node chat)
  const streamingMessageRef = useRef<{
    id: string;
    content: string;
  } | null>(null);

  // Track mode transitions for divider insertion
  const lastEffectiveModeRef = useRef<'general' | 'node' | null>(null);

  const effectiveChatMode: 'general' | 'node' = useMemo(() => {
    // Live node chat can stay connected after canvas deselection; session
    // state must drive routing, not canvas selection alone.
    if (isNodeChatSessionActive || isChatConnected) return 'node';
    return 'general';
  }, [isNodeChatSessionActive, isChatConnected]);

  useEffect(() => {
    if (isChatConnected) {
      setIsNodeChatSessionActive(true);
    }
  }, [isChatConnected]);

  // Insert divider messages on mode transitions + reconnect general chat WS if needed
  useEffect(() => {
    const prevMode = lastEffectiveModeRef.current;
    if (prevMode !== null && prevMode !== effectiveChatMode) {
      insertDivider(
        effectiveChatMode === 'node'
          ? '--- **Live node chat** — the workflow is paused at this node. You can chat with the node here to **steer its output**; use **Resume workflow** when you are done. ---'
          : '--- Switched back to **general chat** — you can configure the node (system prompt, task, name, description, etc.)',
      );
    }
    lastEffectiveModeRef.current = effectiveChatMode;

    if (effectiveChatMode === 'general' && selectedNodeId) {
      ensureConnection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveChatMode]);

  // Subscribe to chat WebSocket messages (live node chat)
  useEffect(() => {
    const unsubscribe = chatWebsocketService.subscribeToAll(
      (message: ChatMessage) => {
        if (message.event_type === 'chat_connected') {
          setChatConnected(true);
          setChatDenied(false);
          setChatAwaitingInput(true);
          setIsChatLoading(false);

          setIsNodeChatSessionActive((wasActive) => {
            if (!wasActive) {
              const promptMessage: ChatMessageData = {
                id: uuidv4(),
                role: 'assistant',
                content:
                  'Ask me to make any change! If you\'re happy with the output, click "Resume workflow" to end chat and continue running the workflow.',
                timestamp: new Date(),
              };
              applyMessage((prev) => [...prev, promptMessage]);
            }
            return true;
          });
          return;
        }

        if (message.event_type === 'chat_denied') {
          setChatDenied(true);
          setIsChatLoading(false);
          return;
        }

        if (message.event_type === 'chat_disconnected') {
          setChatConnected(false);
          setChatDenied(false);
          setChatAwaitingInput(false);
          setIsChatLoading(false);
          setIsNodeChatSessionActive(false);
        }

        if (message.event_type === NodeEventType.AWAITING_USER_INPUT) {
          setChatAwaitingInput(true);
          setIsChatLoading(false);
          return;
        }

        const isAgentMessage = message.display_type === 'chat_agent_message';
        const isThinking = message.display_type === 'thinking';
        const isLegacyChatMessage = message.message_type === 'chat_message';

        if (isAgentMessage || isThinking || isLegacyChatMessage) {
          const newContent =
            typeof message.data === 'string'
              ? message.data
              : (message.message ?? '');

          const messageId =
            message.block_id ||
            message.event_id ||
            message.request_id ||
            uuidv4();

          const isStreaming = message.mode === 'textstream';
          const messageVariant = isThinking ? 'thinking' : 'default';

          if (isStreaming && streamingMessageRef.current?.id === messageId) {
            const accumulatedContent = accumulateStreamingText(
              streamingMessageRef.current.content,
              newContent,
            );

            streamingMessageRef.current.content = accumulatedContent;

            applyMessage((prev) =>
              prev.map((msg) =>
                msg.id === messageId
                  ? {
                      ...msg,
                      content: accumulatedContent,
                      variant: messageVariant,
                    }
                  : msg,
              ),
            );
          } else if (isStreaming) {
            streamingMessageRef.current = {
              id: messageId,
              content: newContent,
            };

            const agentMessage: ChatMessageData = {
              id: messageId,
              role: 'assistant',
              content: newContent,
              timestamp: new Date(),
              variant: messageVariant,
            };
            applyMessage((prev) => [...prev, agentMessage]);
          } else {
            streamingMessageRef.current = null;

            const agentMessage: ChatMessageData = {
              id: messageId,
              role: 'assistant',
              content: newContent,
              timestamp: new Date(),
              variant: messageVariant,
            };
            applyMessage((prev) => [...prev, agentMessage]);
          }

          setIsChatLoading(false);
        }

        if (
          message.message_type === 'end_chat' ||
          message.event_type === 'execution_successful' ||
          message.event_type === 'end'
        ) {
          streamingMessageRef.current = null;
          setIsChatLoading(false);
        }
      },
    );

    return () => {
      unsubscribe();
    };
  }, [setChatConnected, setChatDenied, setChatAwaitingInput, applyMessage]);

  // Subscribe to websocket disconnection (to reset UI state)
  useEffect(() => {
    const unsubscribe = chatWebsocketService.onConnectionStateChange(
      (state: ChatConnectionState) => {
        if (!state.isConnected && isChatConnected) {
          resetChatUI();
          setIsNodeChatSessionActive(false);
        }
      },
    );

    return unsubscribe;
  }, [isChatConnected, resetChatUI]);

  const handleContinue = useCallback(() => {
    if (chatWebsocketService.isConnected()) {
      const userMessage: ChatMessageData = {
        id: uuidv4(),
        role: 'user',
        content: 'Resume workflow',
        timestamp: new Date(),
      };
      applyMessage((prev) => [...prev, userMessage]);

      setChatAwaitingInput(false);
      setIsChatLoading(true);
      chatWebsocketService.send({
        message_type: 'end_chat',
      });

      hidePreview();
    }
  }, [setChatAwaitingInput, hidePreview, applyMessage]);

  // Returns false when the session is gone (nothing went out), so callers
  // -- the prompt-queue dispatch in particular -- can keep the message
  // instead of losing it to a silent no-op.
  const sendNodeChatMessage = useCallback(
    (message: string): boolean => {
      if (!chatWebsocketService.isConnected()) return false;
      setIsChatLoading(true);
      setChatAwaitingInput(false);
      chatWebsocketService.send({
        message_type: 'chat_message',
        message,
      });
      return true;
    },
    [setChatAwaitingInput],
  );

  return {
    effectiveChatMode,
    isChatConnecting,
    isChatConnected,
    isChatDenied,
    isChatAwaitingInput,
    isChatLoading,
    handleContinue,
    sendNodeChatMessage,
  };
}
